/**
 * SchoolSignup.jsx
 *
 * Public self-serve school registration page.
 * Shown when the URL contains ?signup or #signup.
 *
 * Flow:
 *   1. Fill in school name, admin name, email, password, country
 *   2. POST /v1/schools/register
 *   3. On success → call onSuccess({ schoolSlug, adminEmail })
 */

import { useState } from "react";
import { COLORS } from "../lib/colors";

const API = import.meta.env.VITE_API_URL || "https://relay-backend-app.fly.dev";

const COUNTRIES = [
  "Finland", "Sweden", "Norway", "Denmark", "Germany", "United Kingdom",
  "Ireland", "Netherlands", "Belgium", "France", "Spain", "Italy",
  "Poland", "Czech Republic", "Austria", "Switzerland", "United States",
  "Canada", "Australia", "New Zealand", "Other",
];

export default function SchoolSignup({ onSuccess, onGoToLogin }) {
  const [form, setForm] = useState({
    school_name: "",
    admin_name: "",
    admin_email: "",
    password: "",
    country: "",
    domain: "",
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.school_name.trim()) return setError("School name is required.");
    if (!form.admin_name.trim()) return setError("Your name is required.");
    if (!form.admin_email.trim()) return setError("Email address is required.");
    if (form.password.length < 8) return setError("Password must be at least 8 characters.");

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/v1/schools/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          school_name: form.school_name.trim(),
          admin_name: form.admin_name.trim(),
          admin_email: form.admin_email.trim().toLowerCase(),
          password: form.password,
          country: form.country || null,
          domain: form.domain.trim() || null,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.detail || "Registration failed. Please try again.");
        return;
      }

      onSuccess({
        schoolSlug: data.school_slug,
        schoolId: data.school_id,
        adminEmail: form.admin_email.trim().toLowerCase(),
        adminName: form.admin_name.trim(),
        schoolName: form.school_name.trim(),
        password: form.password,
      });
    } catch {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const input = {
    width: "100%",
    padding: "10px 14px",
    borderRadius: 8,
    border: `1px solid ${COLORS.border}`,
    fontSize: 14,
    fontFamily: "inherit",
    outline: "none",
    backgroundColor: COLORS.surface,
    color: COLORS.text,
    boxSizing: "border-box",
  };

  const label = {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.textSecondary,
    marginBottom: 6,
  };

  const row = { marginBottom: 16 };

  return (
    <div style={{
      fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
      backgroundColor: COLORS.bg,
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 20,
    }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />

      <div style={{ width: "100%", maxWidth: 460 }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12, backgroundColor: COLORS.accent,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontWeight: 700, fontSize: 22, marginBottom: 12,
          }}>R</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: COLORS.text, margin: 0 }}>
            Set up your school on Relay
          </h1>
          <p style={{ fontSize: 14, color: COLORS.textSecondary, margin: "8px 0 0" }}>
            Free to get started. Takes 2 minutes.
          </p>
        </div>

        {/* Form card */}
        <div style={{
          backgroundColor: COLORS.surface,
          borderRadius: 12,
          border: `1px solid ${COLORS.border}`,
          padding: 32,
        }}>
          <form onSubmit={handleSubmit} noValidate>
            <div style={row}>
              <label style={label}>School / university name</label>
              <input
                style={input}
                placeholder="e.g. University of Helsinki"
                value={form.school_name}
                onChange={set("school_name")}
                required
                autoFocus
              />
            </div>

            {/* Two-column row for name + email */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              <div>
                <label style={label}>Your name</label>
                <input
                  style={input}
                  placeholder="Jane Doe"
                  value={form.admin_name}
                  onChange={set("admin_name")}
                  required
                />
              </div>
              <div>
                <label style={label}>Country</label>
                <select
                  style={{ ...input, cursor: "pointer" }}
                  value={form.country}
                  onChange={set("country")}
                >
                  <option value="">Select…</option>
                  {COUNTRIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={row}>
              <label style={label}>Work email</label>
              <input
                style={input}
                type="email"
                placeholder="you@university.edu"
                value={form.admin_email}
                onChange={set("admin_email")}
                required
              />
            </div>

            <div style={row}>
              <label style={label}>Password</label>
              <input
                style={input}
                type="password"
                placeholder="At least 8 characters"
                value={form.password}
                onChange={set("password")}
                required
              />
            </div>

            {/* Optional domain */}
            <div style={{ ...row, marginBottom: 24 }}>
              <label style={label}>
                School email domain{" "}
                <span style={{ color: COLORS.textMuted, fontWeight: 400 }}>(optional)</span>
              </label>
              <input
                style={input}
                placeholder="e.g. helsinki.fi"
                value={form.domain}
                onChange={set("domain")}
              />
              <p style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 5, marginBottom: 0 }}>
                Used to auto-verify colleagues on your careers team.
              </p>
            </div>

            {error && (
              <div style={{
                backgroundColor: COLORS.redLight,
                border: `1px solid ${COLORS.red}`,
                borderRadius: 8,
                padding: "10px 14px",
                fontSize: 13,
                color: COLORS.red,
                marginBottom: 16,
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              style={{
                width: "100%",
                padding: "12px 0",
                borderRadius: 8,
                border: "none",
                backgroundColor: submitting ? COLORS.textMuted : COLORS.accent,
                color: "#fff",
                fontSize: 15,
                fontWeight: 600,
                fontFamily: "inherit",
                cursor: submitting ? "not-allowed" : "pointer",
                transition: "background 0.15s",
              }}
            >
              {submitting ? "Setting up…" : "Create school account →"}
            </button>
          </form>
        </div>

        {/* Already have an account */}
        <p style={{ textAlign: "center", fontSize: 13, color: COLORS.textSecondary, marginTop: 20 }}>
          Already have an account?{" "}
          <button
            onClick={onGoToLogin}
            style={{
              background: "none", border: "none", color: COLORS.accent,
              fontSize: 13, fontWeight: 600, cursor: "pointer", padding: 0,
            }}
          >
            Log in
          </button>
        </p>

        {/* Trust signal */}
        <p style={{
          textAlign: "center", fontSize: 12, color: COLORS.textMuted,
          marginTop: 12,
        }}>
          Student data stays within your school. No data shared without consent.
        </p>
      </div>
    </div>
  );
}
